import axios from 'axios';
import { API_BASE_URL } from '../constants/config';
import { handleApiError } from './errorHandler';

/**
 * Normalize ID parameter - handles both numeric IDs and dashless UUID strings.
 * parseInt on a UUID like "550e8400e29b41d4a716446655440000" returns a partial
 * number (550), so we need to detect UUIDs before parsing.
 */
const normalizeId = (id) => {
  if (typeof id === 'string' && /^[0-9a-f]{32}$/i.test(id)) return id;
  return parseInt(id, 10);
};

// Create axios instance with base URL
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add JWT token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token expiration, network errors, and common errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle API errors globally (shows notifications, redirects on 401)
    handleApiError(error);
    // Always reject the error so individual API calls can handle errors if needed
    return Promise.reject(error);
  }
);

// Authentication API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  getCurrentUser: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  // SSO config probe uses bare axios (not the shared instance) so an
  // unreachable backend doesn't trigger the global error toast on the
  // login page — the SSO button just stays hidden.
  getSsoConfig: () => axios.get(`${API_BASE_URL}/auth/sso/config`),
  ssoValidate: (ticket, state) => api.post('/auth/sso/validate', { ticket, state }),
};

// Users API
export const usersAPI = {
  getUsers: () => api.get('/users'),
  getUserById: (id) => api.get(`/users/${normalizeId(id)}`),
  getUserByUsername: (username) => api.get(`/users/by-username/${encodeURIComponent(username)}`),
  createUser: (data) => api.post('/users', data),
  updateUser: (id, data) => api.put(`/users/${normalizeId(id)}`, data),
  updatePassword: (id, currentPassword, newPassword) => api.put(`/users/${normalizeId(id)}/password`, { currentPassword, newPassword }),
  deleteUser: (id) => api.delete(`/users/${normalizeId(id)}`),
  permanentDeleteUser: (id) => api.delete(`/users/${normalizeId(id)}/permanent`),
  adminResetPassword: (id, newPassword) => api.put(`/users/${normalizeId(id)}/password`, { newPassword }),
  updateToken: (id) => api.put(`/users/${normalizeId(id)}/token`),
  uploadAvatar: (id, formData) => api.put(`/users/${normalizeId(id)}/avatar`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  deleteAvatar: (id) => api.delete(`/users/${normalizeId(id)}/avatar`),
  enrollTrainee: (userId, moduleId) => api.post('/users/enroll', { traineeId: normalizeId(userId), moduleId: normalizeId(moduleId) }),
  getUserModules: (userId) => api.get(`/users/${normalizeId(userId)}/modules`),
};

// Content API
export const contentAPI = {
  getContent: () => api.get('/content'),
  getContentById: (id) => api.get(`/content/${normalizeId(id)}`),
  createContent: (data) => api.post('/content', data),
  createContentWithFiles: (formData) => api.post('/content/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  updateContent: (id, data) => api.put(`/content/${normalizeId(id)}`, data),
  deleteContent: (id) => api.delete(`/content/${normalizeId(id)}`),
  uploadAPK: (id, formData) => api.post(`/content/${normalizeId(id)}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  uploadThumbnail: (id, formData) => api.post(`/content/${normalizeId(id)}/thumbnail`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  deleteThumbnail: (id) => api.delete(`/content/${normalizeId(id)}/thumbnail`),
  downloadAPK: (id) => api.get(`/content/${normalizeId(id)}/download`, { responseType: 'blob' }),
  completeContent: (id) => api.post(`/content/${normalizeId(id)}/complete`),
  getCategories: () => api.get('/content/categories'),
  getPlatforms: () => api.get('/content/platforms'),
  getStats: () => api.get('/content/stats'),
};

// Explainer Videos API
export const explainerVideosAPI = {
  getVideos: (params) => api.get('/explainer-videos', { params }),
  getVideoById: (id) => api.get(`/explainer-videos/${normalizeId(id)}`),
  createVideo: (data) => api.post('/explainer-videos', data),
  updateVideo: (id, data) => api.put(`/explainer-videos/${normalizeId(id)}`, data),
  deleteVideo: (id) => api.delete(`/explainer-videos/${normalizeId(id)}`),
  uploadThumbnail: (id, formData) => api.post(`/explainer-videos/${normalizeId(id)}/thumbnail`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  deleteThumbnail: (id) => api.delete(`/explainer-videos/${normalizeId(id)}/thumbnail`),
  getCategories: () => api.get('/explainer-videos/categories'),
  getStats: () => api.get('/explainer-videos/stats'),
  getContentExplainerVideos: (contentId) => api.get(`/explainer-videos/content/${normalizeId(contentId)}/explainer-videos`),
  addVideoToContent: (contentId, videoId, orderIndex = 0) => api.post(`/explainer-videos/content/${normalizeId(contentId)}/explainer-videos`, { videoId: normalizeId(videoId), orderIndex }),
  removeVideoFromContent: (contentId, videoId) => api.delete(`/explainer-videos/content/${normalizeId(contentId)}/explainer-videos/${normalizeId(videoId)}`),
};

// Modules API
export const modulesAPI = {
  getModules: () => api.get('/modules'),
  getModuleById: (id) => api.get(`/modules/${normalizeId(id)}`),
  createModule: (data) => api.post('/modules', data),
  updateModule: (id, data) => api.put(`/modules/${normalizeId(id)}`, data),
  deleteModule: (id) => api.delete(`/modules/${normalizeId(id)}`),
  addContentToModule: (moduleId, contentId) => api.post(`/modules/${normalizeId(moduleId)}/content`, { contentId: normalizeId(contentId) }),
  removeContentFromModule: (moduleId, contentId) => api.delete(`/modules/${normalizeId(moduleId)}/content/${normalizeId(contentId)}`),
  addVirtualTourToModule: (moduleId, tourId) => api.post(`/modules/${normalizeId(moduleId)}/virtual-tours`, { tourId: normalizeId(tourId) }),
  removeVirtualTourFromModule: (moduleId, tourId) => api.delete(`/modules/${normalizeId(moduleId)}/virtual-tours/${normalizeId(tourId)}`),
  assignInstructor: (moduleId, instructorId) => api.post(`/modules/${normalizeId(moduleId)}/instructors`, { instructorId: normalizeId(instructorId) }),
  removeInstructor: (moduleId, instructorId) => api.delete(`/modules/${normalizeId(moduleId)}/instructors/${normalizeId(instructorId)}`),
  enrollTrainee: (moduleId, traineeId) => api.post(`/modules/${normalizeId(moduleId)}/enroll`, { traineeId: normalizeId(traineeId) }),
  unenrollTrainee: (moduleId, traineeId) => api.delete(`/modules/${normalizeId(moduleId)}/enroll/${normalizeId(traineeId)}`),
  getModuleProgress: (id) => api.get(`/modules/${normalizeId(id)}/progress`),
  getModuleTrainees: (id) => api.get(`/modules/${normalizeId(id)}/trainees`),
  getCategories: () => api.get('/modules/categories'),
  getLevels: () => api.get('/modules/levels'),
};

// Analytics API
export const analyticsAPI = {
  getOverview: () => api.get('/analytics/overview'),
  getModuleAnalytics: (moduleId) => api.get(`/analytics/module/${normalizeId(moduleId)}`),
  getPersonalAnalytics: () => api.get('/analytics/personal'),
  getPersonalModuleAnalytics: (moduleId) => api.get(`/analytics/personal/module/${normalizeId(moduleId)}`),
  getContentAnalytics: () => api.get('/analytics/content'),
  getDashboardStats: () => api.get('/dashboard/stats'),
  getTraineeAnalytics: (traineeId) => api.get(`/analytics/trainee/${normalizeId(traineeId)}`),
  getTraineeModuleAnalytics: (traineeId, moduleId) =>
    api.get(`/analytics/trainee/${normalizeId(traineeId)}/module/${normalizeId(moduleId)}`),
  getTraineeContentAnalytics: (traineeId, moduleId, contentId) =>
    api.get(`/analytics/trainee/${normalizeId(traineeId)}/module/${normalizeId(moduleId)}/content/${normalizeId(contentId)}`),
};

// Notifications API
export const notificationsAPI = {
  getNotifications: (params) => api.get('/notifications', { params }),
  markAsRead: (id) => api.patch(`/notifications/${normalizeId(id)}/read`),
  markAllAsRead: () => api.patch('/notifications/read-all'),
};

// Enrollment API
export const enrollmentsAPI = {
  bulkEnroll: (data) => api.post('/enrollments/bulk', data),
  getUserEnrollments: (userId) => api.get(`/enrollments/user/${normalizeId(userId)}`),
  updateProgress: (userId, moduleId, progress) => api.put(`/enrollments/${normalizeId(userId)}/${normalizeId(moduleId)}`, { progress }),
  completeModule: (userId, moduleId) => api.post(`/enrollments/${normalizeId(userId)}/${normalizeId(moduleId)}/complete`),
};

// System API
export const systemAPI = {
  healthCheck: () => api.get('/system/health'),
  getConfig: () => api.get('/system/config'),
  updateConfig: (data) => api.put('/system/config', data),
  getPermissions: () => api.get('/system/permissions'),
};

// VR Access API (Meta Developer Hub release channels)
export const vrAccessAPI = {
  // Channels
  listChannels: (params) => api.get('/vr-access/channels', { params }),
  getChannel: (id) => api.get(`/vr-access/channels/${normalizeId(id)}`),
  createChannel: (data) => api.post('/vr-access/channels', data),
  updateChannel: (id, data) => api.put(`/vr-access/channels/${normalizeId(id)}`, data),
  deleteChannel: (id) => api.delete(`/vr-access/channels/${normalizeId(id)}`),

  // Users
  listUsers: (params) => api.get('/vr-access/users', { params }),
  getUser: (id) => api.get(`/vr-access/users/${normalizeId(id)}`),
  createUser: (data) => api.post('/vr-access/users', data),
  updateUser: (id, data) => api.put(`/vr-access/users/${normalizeId(id)}`, data),
  resendInvite: (id) => api.post(`/vr-access/users/${normalizeId(id)}/resend`),
  revokeInvite: (id) => api.post(`/vr-access/users/${normalizeId(id)}/revoke`),
  deleteUser: (id) => api.delete(`/vr-access/users/${normalizeId(id)}`),
};

// Virtual Tours API (curated web-based virtual tours)
export const virtualToursAPI = {
  getTours: (params) => api.get('/virtual-tours', { params }),
  getTourById: (id) => api.get(`/virtual-tours/${normalizeId(id)}`),
  createTour: (data) => api.post('/virtual-tours', data),
  updateTour: (id, data) => api.put(`/virtual-tours/${normalizeId(id)}`, data),
  deleteTour: (id) => api.delete(`/virtual-tours/${normalizeId(id)}`),
  getCategories: () => api.get('/virtual-tours/categories'),
  uploadImage: (formData) => api.post('/virtual-tours/upload-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
};

export default api;